import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import RecordOrganizer from "@/components/RecordOrganizer";
import { toast } from "sonner";
import { Trash, Receipt, ArrowsClockwise, PencilSimple } from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";

const CURRENCIES = ["USD", "GBP", "EUR", "TRY"];
const CURRENCY_SYMBOL = { USD: "$", GBP: "£", EUR: "€", TRY: "₺" };
const INCOTERMS = ["EXW", "FCA", "FAS", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
const SHIP_METHODS = [
  "Sea Freight - FCL", "Sea Freight - LCL", "Air Freight",
  "Road / Truck", "Rail", "Courier / Express",
];

function InvoiceList() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm(t("final.delete_confirm"))) return;
    try {
      await api.delete(`/invoices/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success(t("final.delete_success"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <RecordOrganizer recordType="invoice" items={items} setItems={setItems} numberField="invoice_no" dateField="date">
      {({ filteredItems, selected, toggle, toggleAll, allVisibleSelected, categories }) => {
        const categoryMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));
        return <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="data-table w-full min-w-[1020px]">
            <thead><tr>
              <th className="w-10 text-center"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Tüm görünen faturaları seç" /></th>
              <th>{t("invoice.invoice_no")}</th><th>Kategori</th><th>{t("invoice.date")}</th><th>{t("invoice.plan_no")}</th><th>{t("invoice.consignee")}</th>
              <th>INCOTERMS</th><th className="text-right">{t("invoice.quantity")}</th>
              <th className="text-right">{t("invoice.amount")}</th><th>{t("invoice.status")}</th><th></th>
            </tr></thead>
            <tbody>
              {filteredItems.map((inv) => (
                <tr key={inv.id} className={`${inv.status === "cancelled" ? "opacity-60" : ""} ${selected.has(inv.id) ? "bg-blue-50" : ""}`}
                  data-testid={`invoice-row-${inv.invoice_no}`}>
                  <td className="text-center"><input type="checkbox" checked={selected.has(inv.id)} onChange={()=>toggle(inv.id)} aria-label={`${inv.invoice_no} seç`} /></td>
                  <td className="font-mono text-xs"><Link to={`/invoices/${inv.id}`} className="text-slate-900 font-semibold hover:underline">{inv.invoice_no}</Link></td>
                  <td><span className={`text-[10px] px-2 py-1 border ${inv.category_id ? "bg-slate-50 text-slate-700" : "text-slate-400"}`}>{categoryMap[inv.category_id] || "Kategorisiz"}</span></td>
                  <td className="font-mono text-xs">{inv.date}</td>
                  <td className="font-mono text-xs text-slate-500">{inv.plan_no}</td>
                  <td className="truncate max-w-xs">{inv.consignee || inv.shipment_name}</td>
                  <td className="font-mono text-xs">{inv.incoterms}</td>
                  <td className="text-right data-cell">{inv.total_qty}</td>
                  <td className="text-right data-cell font-mono">{CURRENCY_SYMBOL[inv.currency] || ""}{Number(inv.grand_total ?? inv.subtotal ?? 0).toFixed(2)}</td>
                  <td><span className={`text-[10px] uppercase px-2 py-0.5 font-semibold border ${inv.status === "cancelled" ? "bg-slate-100 text-slate-500 border-slate-300 line-through" : "bg-slate-900 text-white border-slate-900"}`}>{inv.status === "cancelled" ? t("invoice.cancelled") : t("invoice.active")}</span></td>
                  <td className="text-right"><button onClick={() => remove(inv.id)} className="text-slate-400 hover:text-red-600" data-testid={`del-inv-${inv.invoice_no}`}><Trash size={16} /></button></td>
                </tr>
              ))}
              {filteredItems.length === 0 && <tr><td colSpan={11} className="text-center py-12 text-slate-400">Filtreye uygun fatura bulunamadı.</td></tr>}
            </tbody>
          </table>
        </div>;
      }}
    </RecordOrganizer>
  );
}

function NewInvoice({ onCreated }) {
  const { t } = useI18n();
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [products, setProducts] = useState({});
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState({
    invoice_no: "",
    date: new Date().toLocaleDateString("en-CA"),
    incoterms: "FOB",
    shipping_method: "Sea Freight - FCL",
    carrier: "",
    seller_exporter: "",
    currency: "USD",
    note: "",
    convert_prices: false,
    transport_cost: 0,
    insurance_cost: 0,
    allocate_additional_costs: false,
  });
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/invoices/eligible-plans").then((r) => setPlans(r.data));
    api.get("/products").then((r) => {
      const m = {};
      r.data.forEach((p) => { m[p.sku] = p; });
      setProducts(m);
    });
    api.get("/settings").then((r) => {
      if (r.data.company_name) setForm((f) => ({ ...f, seller_exporter: r.data.company_name }));
    });
    api.get("/exchange-rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const showTransportCost = form.incoterms !== "EXW";
  const showInsuranceCost = ["CIF", "CIP", "DPU", "DAP", "DDP"].includes(form.incoterms);

  const convertPrice = (price, from, to) => {
    if (!rates || !rates.rates || price === 0 || from === to) return price;
    const r = rates.rates;
    if (!r[from] || !r[to]) return price;
    return Math.round(((price * r[from]) / r[to]) * 100) / 100;
  };

  const preview = useMemo(() => {
    if (!selectedPlan) return null;
    const agg = {};
    for (const pl of selectedPlan.pallets || []) {
      for (const b of pl.boxes || []) {
        if (!agg[b.sku]) {
          const p = products[b.sku] || {};
          const origPrice = Number(p.price || 0);
          const prodCcy = p.currency || "USD";
          const price = form.convert_prices
            ? convertPrice(origPrice, prodCcy, form.currency)
            : origPrice;
          agg[b.sku] = {
            sku: b.sku,
            description: p.description || `${b.inch}"`,
            qty: 0,
            price,
            original_price: origPrice,
            product_currency: prodCcy,
            converted: form.convert_prices && origPrice > 0 && prodCcy !== form.currency,
            currency_mismatch: !form.convert_prices && prodCcy !== form.currency && origPrice > 0,
          };
        }
        agg[b.sku].qty += 1;
      }
    }
    const baseItems = Object.values(agg);
    const baseSubtotal = baseItems.reduce((s, it) => s + it.qty * it.price, 0);
    const transportCost = showTransportCost ? Math.max(0, Number(form.transport_cost || 0)) : 0;
    const insuranceCost = showInsuranceCost ? Math.max(0, Number(form.insurance_cost || 0)) : 0;
    const additionalCost = transportCost + insuranceCost;
    const grandTotal = baseSubtotal + additionalCost;
    const ratio = form.allocate_additional_costs && baseSubtotal > 0 ? grandTotal / baseSubtotal : 1;
    const items = baseItems.map((it) => ({
      ...it,
      base_price: it.price,
      price: form.allocate_additional_costs ? it.price * ratio : it.price,
      allocated: form.allocate_additional_costs && additionalCost > 0,
    }));
    const totalQty = items.reduce((s, it) => s + it.qty, 0);
    return {
      items, subtotal: grandTotal, baseSubtotal, totalQty, transportCost, insuranceCost, additionalCost, ratio,
      hasMismatch: items.some((it) => it.currency_mismatch),
    };
  }, [selectedPlan, products, form.currency, form.convert_prices, form.incoterms, form.transport_cost, form.insurance_cost, form.allocate_additional_costs, rates, showTransportCost, showInsuranceCost]);

  const submit = async () => {
    if (!selectedPlanId) return toast.error("Bir onaylı plan seçin");
    setCreating(true);
    try {
      const payload = { plan_id: selectedPlanId, ...form };
      if (!payload.invoice_no) delete payload.invoice_no;
      const r = await api.post("/invoices", payload);
      toast.success(`Fatura oluşturuldu: ${r.data.invoice_no}`);
      onCreated();
      nav(`/invoices/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <div className="xl:col-span-1 space-y-4">
        <div className="bg-white border border-slate-200 p-5 space-y-3">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">
            {t("invoice.select_approved")}
          </h3>
          <select data-testid="select-approved-plan" className="input"
            value={selectedPlanId} onChange={(e) => setSelectedPlanId(e.target.value)}>
            <option value="">{t("invoice.select_plan")}</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.plan_no} · {p.consignee || p.shipment_name} · {p.country} ({p.pallets?.length || 0} palet)
              </option>
            ))}
          </select>
          {plans.length === 0 && (
            <div className="text-xs text-slate-600 bg-slate-100 border border-slate-300 px-3 py-2">
              {t("invoice.no_approved")}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 p-5 space-y-3">
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">
            {t("invoice.details")}
          </h3>
          <label className="text-xs font-semibold block">Fatura Tarihi<input type="date" data-testid="inv-date" className="input mt-1" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></label>
          <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 block">
            {t("invoice.invoice_no")} <span className="normal-case text-slate-400">({t("invoice.auto_no")})</span>
            <input data-testid="inv-invoice-no" className="input mt-1 font-mono"
              placeholder="Otomatik: INV-YYYYMMDD-XXXX"
              value={form.invoice_no}
              onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">
              INCOTERMS
              <select data-testid="inv-incoterms" className="input mt-1"
                value={form.incoterms} onChange={(e) => setForm({ ...form, incoterms: e.target.value })}>
                {INCOTERMS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">
              {t("invoice.currency")}
              <select data-testid="inv-currency" className="input mt-1"
                value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{CURRENCY_SYMBOL[c]} {c}</option>
                ))}
              </select>
            </label>
          </div>
          {showTransportCost && (
            <div className={`grid ${showInsuranceCost ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
              <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">
                TRANSPORT COST ({form.currency})
                <input data-testid="inv-transport-cost" className="input mt-1 font-mono" type="number" min="0" step="0.01"
                  value={form.transport_cost} onChange={(e) => setForm({ ...form, transport_cost: e.target.value })} />
              </label>
              {showInsuranceCost && (
                <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">
                  INSURANCE ({form.currency})
                  <input data-testid="inv-insurance-cost" className="input mt-1 font-mono" type="number" min="0" step="0.01"
                    value={form.insurance_cost} onChange={(e) => setForm({ ...form, insurance_cost: e.target.value })} />
                </label>
              )}
            </div>
          )}
          {showTransportCost && (
            <label className="flex items-start gap-2 text-xs cursor-pointer border border-slate-200 bg-slate-50 p-3" data-testid="inv-allocate-costs">
              <input type="checkbox" checked={form.allocate_additional_costs}
                onChange={(e) => setForm({ ...form, allocate_additional_costs: e.target.checked })} className="mt-0.5" />
              <span className="text-slate-700">
                <b>Ek maliyetler ürün fiyatına göre orantılı olarak eklensin</b>
                <div className="text-slate-500 mt-0.5">Transport Cost ve varsa Insurance, ürünlerin mevcut toplam tutar paylarına göre birim fiyatlara dağıtılır.</div>
                <div className="text-slate-500 mt-0.5">Incoterms @ 2020 Teslimat Şekillerine göre C ve D grubu gönderilerinde checkbox'ı işaretlemeniz önerilir</div>
              </span>
            </label>
          )}
          <label className="flex items-start gap-2 text-xs cursor-pointer" data-testid="inv-convert-toggle">
            <input type="checkbox" checked={form.convert_prices}
              onChange={(e) => setForm({ ...form, convert_prices: e.target.checked })}
              className="mt-0.5" />
            <span className="text-slate-700">
              <b>{t("invoice.convert")}</b>
              <div className="text-slate-500 mt-0.5">
                {t("invoice.convert_desc")}
                {rates && rates.rates && (
                  <span className="ml-1 text-slate-400 font-mono text-[10px]">
                    ({rates.source} {rates.date} — 1$={rates.rates.USD}₺, 1€={rates.rates.EUR}₺, 1£={rates.rates.GBP}₺)
                  </span>
                )}
              </div>
            </span>
          </label>
          <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 block">
            {t("invoice.shipping")}
            <select data-testid="inv-shipping-method" className="input mt-1"
              value={form.shipping_method}
              onChange={(e) => setForm({ ...form, shipping_method: e.target.value })}>
              {SHIP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              <option value="">{t("invoice.other")}</option>
            </select>
          </label>
          {!SHIP_METHODS.includes(form.shipping_method) && (
            <input className="input" placeholder={t("invoice.shipping")}
              value={form.shipping_method}
              onChange={(e) => setForm({ ...form, shipping_method: e.target.value })} />
          )}
          <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 block">
            {t("invoice.carrier")}
            <input data-testid="inv-carrier" className="input mt-1"
              placeholder="Maersk Line / DHL / MSC ..."
              value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} />
          </label>
          <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 block">
            Seller / Exporter
            <input data-testid="inv-seller" className="input mt-1"
              placeholder={t("invoice.seller")}
              value={form.seller_exporter}
              onChange={(e) => setForm({ ...form, seller_exporter: e.target.value })} />
          </label>
          <label className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 block">
            {t("invoice.note")}
            <textarea data-testid="inv-note" className="input mt-1" rows={2}
              value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </label>
        </div>

        <button data-testid="create-invoice-btn"
          onClick={submit} disabled={creating || !selectedPlanId}
          className="w-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white py-3 font-semibold rounded-sm inline-flex items-center justify-center gap-2">
          <Receipt size={18} weight="fill" /> {creating ? t("invoice.creating") : t("invoice.create")}
        </button>
      </div>

      <div className="xl:col-span-2">
        <div className="bg-white border border-slate-200">
          <div className="border-b border-slate-200 px-5 py-3 flex justify-between items-center">
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-700">
              {t("invoice.preview")}
            </h3>
            {preview && (
              <div className="text-xs font-mono text-slate-500">
                {preview.items.length} SKU · {preview.totalQty} adet ·
                <b className="ml-1 text-slate-900">
                  {CURRENCY_SYMBOL[form.currency]}{preview.subtotal.toFixed(2)}
                </b>
              </div>
            )}
          </div>
          {!preview ? (
            <div className="p-16 text-center text-slate-400 text-sm">{t("invoice.preview_hint")}</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="data-table w-full min-w-[640px]">
              <thead><tr>
                <th>SKU</th><th>{t("invoice.description")}</th>
                <th className="text-right">{t("invoice.quantity")}</th>
                <th className="text-right">{t("invoice.unit_price")}</th>
                <th className="text-right">{t("invoice.total")}</th>
              </tr></thead>
              <tbody>
                {preview.items.map((it) => (
                  <tr key={it.sku} className={it.currency_mismatch ? "bg-slate-50" : ""}>
                    <td className="font-mono text-xs font-bold">
                      {it.sku}
                      {it.currency_mismatch && (
                        <span className="ml-2 text-[9px] font-semibold text-slate-700 bg-slate-200 border border-slate-400 px-1 py-0.5 uppercase" title={`Ürün fiyatı ${it.product_currency} ile tanımlı`}>
                          {it.product_currency}
                        </span>
                      )}
                      {it.converted && (
                        <span className="ml-2 text-[9px] font-semibold text-slate-900 bg-slate-100 border border-slate-900 px-1 py-0.5 uppercase" title={`Orijinal: ${CURRENCY_SYMBOL[it.product_currency]}${it.original_price} ${it.product_currency}`}>
                          çevrildi
                        </span>
                      )}
                    </td>
                    <td>{it.description}</td>
                    <td className="text-right data-cell">{it.qty}</td>
                    <td className="text-right data-cell font-mono">
                      {it.price === 0 ? (
                        <span className="text-slate-500 text-[10px]">{t("invoice.no_price")}</span>
                      ) : (
                        <>{CURRENCY_SYMBOL[form.currency]}{it.price.toFixed(it.allocated ? 4 : 2)}</>
                      )}
                    </td>
                    <td className="text-right data-cell font-mono font-bold">
                      {CURRENCY_SYMBOL[form.currency]}{(it.qty * it.price).toFixed(2)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50">
                  <td colSpan={4} className="font-semibold text-right">PRODUCT SUBTOTAL</td>
                  <td className="text-right data-cell font-mono">{CURRENCY_SYMBOL[form.currency]}{preview.baseSubtotal.toFixed(2)}</td>
                </tr>
                {preview.transportCost > 0 && <tr><td colSpan={4} className="font-semibold text-right">TRANSPORT COST</td><td className="text-right data-cell font-mono">{CURRENCY_SYMBOL[form.currency]}{preview.transportCost.toFixed(2)}</td></tr>}
                {preview.insuranceCost > 0 && <tr><td colSpan={4} className="font-semibold text-right">INSURANCE</td><td className="text-right data-cell font-mono">{CURRENCY_SYMBOL[form.currency]}{preview.insuranceCost.toFixed(2)}</td></tr>}
                <tr className="bg-slate-900 text-white">
                  <td colSpan={2} className="font-bold text-right">GRAND TOTAL</td>
                  <td className="text-right data-cell font-bold">{preview.totalQty}</td>
                  <td></td>
                  <td className="text-right data-cell font-bold">{CURRENCY_SYMBOL[form.currency]}{preview.subtotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
            </div>
          )}
          {preview && preview.hasMismatch && (
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-300 text-xs text-slate-700">
              <b>{t("invoice.note")}:</b> Bazı ürünlerin para birimi farklı ({form.currency} değil).
              Yukarıdan <b>"Otomatik döviz çevirisi"</b> seçeneğini işaretleyerek TCMB kuruyla otomatik çevirebilirsiniz.
            </div>
          )}
          {preview && preview.items.some((it) => it.price === 0) && (
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-300 text-xs text-slate-700">
              Bazı ürünlerde fiyat tanımlı değil.
              <Link to="/products" className="mx-1 underline font-semibold">{t("final.invoice.product_management")}</Link>
              sayfasından birim fiyatları girin.
            </div>
          )}
        </div>
      </div>
      <style>{`.input{width:100%;border:1px solid #cbd5e1;padding:0.45rem 0.6rem;border-radius:0.125rem;font-size:0.85rem;outline:none}.input:focus{border-color:#0F172A;box-shadow:0 0 0 3px rgba(15,23,42,0.15)}`}</style>
    </div>
  );
}

export default function CommercialInvoice() {
  const { t } = useI18n();
  const [tick, setTick] = useState(0);
  return (
    <div>
      <PageHeader title={t("invoice.title")}
        subtitle={t("invoice.subtitle")}>
        <Link to="/invoices/designer" data-testid="invoice-designer-link" className="text-xs font-semibold px-3 py-2 bg-slate-900 text-white hover:bg-slate-800">Şablon Tasarla</Link>
        <Link to="/settings" className="text-xs text-slate-600 hover:underline">{t("invoice.settings")}</Link>
      </PageHeader>
      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8">
        <NewInvoice onCreated={() => setTick((t) => t + 1)} />
        <div>
          <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-2">
            {t("invoice.saved")}
          </h3>
          <InvoiceList key={tick} />
        </div>
      </div>
    </div>
  );
}

